import { proxy } from "valtio";
import { Auth } from "./auth";
import { HUD } from "./hud";
import { setArray } from "./utils";

export interface OrgMember {
    principal: string;
    role_name: string;
    granted_at: string;
    last_seen_at: string | null;
    project_count: number;
}

export type OrgRoleName = "admin" | "member" | "guest";

export class Org {
    private readonly auth: Auth;
    private readonly hud: HUD;

    public state = proxy({
        members: null as OrgMember[] | null,
    });

    constructor(auth: Auth, hud: HUD) {
        this.auth = auth;
        this.hud = hud;
    }

    public async loadMembers(): Promise<void> {
        // Only a member can list members
        if (!this.isMember()) {
            return;
        }

        try {
            const response = await this.auth.fetch("/api/org_members");
            if (!response.ok) {
                throw new Error("Failed to load organization members");
            }

            this.state.members = (await response.json()) as OrgMember[];
        } catch {
            this.hud.toast("Failed to load organization members", "error");
        }
    }

    public isMember(): boolean {
        return this.auth.state.orgRole === "org:admin" || this.auth.state.orgRole === "org:member";
    }

    public canManage(): boolean {
        return this.auth.state.orgRole === "org:admin";
    }

    public async updateMemberRole(principal: string, roleName: OrgRoleName): Promise<void> {
        const member = this.state.members?.find((m) => m.principal === principal);
        if (!member) return;

        const prevRole = member.role_name;
        if (prevRole === roleName) return;

        // Optimistically reflect the new role, revert on failure.
        member.role_name = roleName;

        const response = await this.auth.fetch(`/api/org_members/${encodeURIComponent(principal)}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ role_name: roleName }),
        });

        if (!response.ok) {
            member.role_name = prevRole;
            this.hud.toast("Failed to update role", "error");
        }
    }

    public async removeMember(principal: string): Promise<void> {
        const confirmed = confirm(`Remove ${principal} from the organization and all projects?`);
        if (!confirmed) return;

        const response = await this.auth.fetch(`/api/org_members/${encodeURIComponent(principal)}`, {
            method: "DELETE",
        });

        if (!response.ok) {
            this.hud.toast("Failed to remove member", "error");
            return;
        }

        if (this.state.members) {
            setArray(
                this.state.members,
                this.state.members.filter((m) => m.principal !== principal),
            );
        }
        this.hud.toast("Member removed", "success");
    }

    public async create(name: string): Promise<{ slug: string }> {
        const response = await this.auth.fetch("/api/organizations", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name }),
        });

        if (!response.ok) {
            throw new Error("Failed to create organization");
        }

        return (await response.json()) as { slug: string };
    }
}
